/**
 * Banka hareketinden karşı taraf TCKN/VKN çözümleme.
 * Gelen → gönderen (müşteri); Giden → alıcı (müşteri).
 * Firma sahibi / hesap TCKN’si asla gösterilmez.
 */

const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

/** Bilinen hesap sahibi TCKN (DEK / hesap özeti) */
const KNOWN_OWN_ACCOUNT_TCKNS = new Set(['33592419332']);

/** 11 haneli TCKN; 10 haneli VKN ayrı döner. */
export const normalizeTckn = (value) => {
  const digits = normalizeDigits(value);
  return digits.length === 11 ? digits : '';
};

export const normalizeVkn = (value) => {
  const digits = normalizeDigits(value);
  return digits.length === 10 ? digits : '';
};

const readEnrichmentList = (movement) => {
  const raw = movement?.rawData || movement;
  if (Array.isArray(raw?.enrichmentInformation)) return raw.enrichmentInformation;
  if (Array.isArray(movement?.enrichmentInformation)) return movement.enrichmentInformation;
  return [];
};

const readEftEnrichment = (movement) => {
  for (const info of readEnrichmentList(movement)) {
    const code = String(info?.enrichmentCode || '').toUpperCase();
    const val = info?.enrichmentValue || info?.EFT || info;
    if (code === 'EFT' || val?.moduleName === 'EFT') return val;
  }
  const raw = movement?.rawData || movement;
  if (raw?.EFT && typeof raw.EFT === 'object') return raw.EFT;
  return null;
};

/** DEK metnindeki TC KİMLİK NO satırları — hesap özeti tarafı (genelde biz). */
/**
 * DEK metninde karşı tarafı işaret eden başlıklar.
 * "BORÇLU TC KİMLİK NO" ifadesi "TC KİMLİK NO" kalıbını da içerdiği için,
 * önek kontrolü olmadan müşterinin TCKN'si de "bizim" sayılıp gizleniyordu.
 */
const DEK_COUNTERPARTY_PREFIX = /(BOR[CÇ]LU|ALACAKLI|KAR[ŞS]I|G[ÖO]NDEREN|ALICI)\s*$/i;

const extractDekTckns = (movement) => {
  const found = [];
  for (const info of readEnrichmentList(movement)) {
    const text = String(info?.enrichmentValue?.detailText || info?.DEK?.detailText || '');
    if (!text) continue;
    const re = /TC\s*K[İI]ML[İI]K\s*NO\s*:?\s*(\d{11})/gi;
    let m;
    while ((m = re.exec(text)) !== null) {
      // Eşleşmenin hemen öncesi "BORÇLU" / "ALACAKLI" gibi bir etiketse bu
      // hesap sahibinin değil karşı tarafın TCKN'sidir; gizlenmemeli.
      const before = text.slice(Math.max(0, m.index - 24), m.index);
      if (DEK_COUNTERPARTY_PREFIX.test(before)) continue;
      const tckn = normalizeTckn(m[1]);
      if (tckn) found.push(tckn);
    }
  }
  return found;
};

/**
 * Bu hareket satırındaki “bizim” TCKN’ler (gösterilmeyecek / müşteri sanılmayacak).
 */
export function resolveOwnAccountTckns(movement) {
  const own = new Set(KNOWN_OWN_ACCOUNT_TCKNS);
  const accountTckn = normalizeTckn(movement?.rawData?.TCKN || movement?.TCKN);
  if (accountTckn) own.add(accountTckn);
  extractDekTckns(movement).forEach((t) => own.add(t));
  return own;
}

const firstTcknExcluding = (candidates, excludeSet) => {
  for (const value of candidates) {
    const tckn = normalizeTckn(value);
    if (tckn && !excludeSet.has(tckn)) return tckn;
  }
  return '';
};

const firstVknExcluding = (candidates, excludeSet) => {
  for (const value of candidates) {
    const vkn = normalizeVkn(value);
    if (!vkn) continue;
    if (excludeSet.has(vkn)) continue;
    return vkn;
  }
  return '';
};

/**
 * Gelen: gönderenin TCKN’si · Giden: alıcının TCKN’si.
 */
export function resolveCounterpartyTcknFromBankMovement(movement) {
  if (!movement) return '';

  const direction = movement.direction === 'out' ? 'out' : 'in';
  const eft = readEftEnrichment(movement);
  const own = resolveOwnAccountTckns(movement);

  if (direction === 'out') {
    // Alıcı / karşı taraf — gönderen (biz) alanları kullanılmaz
    return firstTcknExcluding(
      [
        movement?.receiverTckn,
        eft?.receiverTCKN,
        eft?.receiverVKN,
        movement?.corrTCKN,
        movement?.counterpartyTckn,
        movement?.rawData?.corrTCKN,
        eft?.corrTCKN
      ],
      own
    );
  }

  // Gelen: gönderen (müşteri)
  return firstTcknExcluding(
    [
      movement?.corrTCKN,
      movement?.counterpartyTckn,
      movement?.rawData?.corrTCKN,
      movement?.senderTckn,
      eft?.senderVKN,
      eft?.senderTCKN,
      eft?.corrTCKN
    ],
    own
  );
}

/**
 * Karşı taraf VKN (firma — 10 hane).
 */
export function resolveCounterpartyVknFromBankMovement(movement) {
  if (!movement) return '';

  const direction = movement.direction === 'out' ? 'out' : 'in';
  const eft = readEftEnrichment(movement);
  const own = resolveOwnAccountTckns(movement);

  if (direction === 'out') {
    return firstVknExcluding(
      [eft?.receiverVKN, movement?.corrVKN, movement?.rawData?.corrVKN, eft?.corrVKN],
      own
    );
  }

  return firstVknExcluding(
    [movement?.corrVKN, movement?.rawData?.corrVKN, eft?.senderVKN, eft?.corrVKN],
    own
  );
}

/**
 * Tabloda / detayda gösterim: TCKN öncelikli, yoksa VKN.
 * @returns {{ tckn: string, vkn: string, display: string, label: string }}
 */
export function resolveCounterpartyTaxIdFromBankMovement(movement) {
  const tckn = resolveCounterpartyTcknFromBankMovement(movement);
  if (tckn) {
    return { tckn, vkn: '', display: tckn, label: 'TCKN' };
  }
  const vkn = resolveCounterpartyVknFromBankMovement(movement);
  if (vkn) {
    return { tckn: '', vkn, display: vkn, label: 'VKN' };
  }
  return { tckn: '', vkn: '', display: '', label: '' };
}

/** Müşteri auto-upsert: kendi TC’mizi müşteri sanma. */
export function isOwnAccountTaxId(movement, taxId) {
  const digits = normalizeDigits(taxId);
  if (!digits) return false;
  return resolveOwnAccountTckns(movement).has(digits);
}
